import { buildLatestEditsQuery } from "components/Charts/LatestEdits/LatestEdits.service";
import { IconCollection } from "components/Icon/IconCollection";
import { buildAssignedCommentsSearchQuery } from "components/Charts/AssignedComments/assignedComments";
import { ResourceConfig } from "config/config";
import { latestListContainerWidget } from "config/resources/sharedFieldsTyped";
import { ExecutionStatus } from "config/ts-utils";
import { genericOverTimeChartDto } from "config/utils.jsx";
import { getWorkspaceExecutionFilterLink } from "containers/ExecutionFilter/utils";
import { executionsListSubscription } from "containers/Executions/Executions.type";
import {
    containerSettingsActionSignature,
    exportActionSignature,
    exportAllActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { bulkCommitActionSignature } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/BulkCommitAction";
import { checkSSPHidden } from "containers/Resources/screens/ResourcesSingle/utils";
import { executionStatusPerTimeQuery } from "containers/Stats/Stats.type";
import { hasOrganizationAdminPermission, UserPermissionList } from "containers/Auth/permissions";
import { User } from "graphql/generated/graphql";
import prettyBytes from "pretty-bytes";
import { advancedSearchPathFragment } from "utils/RouterManager/constants";
import { getWorkspaceLink } from "utils/service";
import {
    advancedFilterResourceList,
    getFilterFieldsList,
    getFilterString,
    getSearchObj,
} from "utils/utils";
import resource from "./resource";

const productiveOnlyFilterField = getSearchObj({ field: "is_productive", op: "eq", value: true });

const executionStatusPerTimeDashboardQuery =
    (variables = {}) =>
    () => ({
        query: executionStatusPerTimeQuery,
        variables: {
            interval: 7,
            per: "day",
            allow_normal: true,
            allow_deleted: false,
            ...variables,
        },
    });

const resourceName = "workspace";

const config: ResourceConfig = {
    label: "Workspace",
    icon: IconCollection.monitorDashboard,
    resourceName,
    resourceClass: "resource",
    ignoreIsReadonly: true,
    isBulkDeleteForbidden: true,
    actions: {
        shown: [bulkCommitActionSignature],
        hidden: [containerSettingsActionSignature, exportActionSignature, exportAllActionSignature],
        hidePin: true,
    },
    layout: [
        {
            partial: "pageHeaderForm",
            config: {
                editIcon: true,
                editName: false,
            },
        },
        // Directly below the record header & actions: the dashboard tab strip
        // (Default dashboard + any additional dashboards). When an additional
        // dashboard tab is active it suppresses the form/dashboard/SearchTabs
        // partials below so only that dashboard shows.
        { partial: "containerDashboards" },
        { partial: "form" },
        { partial: "dashboard" },
        {
            partial: "SearchTabs",
            name: "resourceList",
            filter: () => ({
                and: [
                    { field: "project_id", op: "eq", value: null },
                    { field: "bundle_id", op: "eq", value: null },
                ],
            }),
        },
    ],
    graphql: {
        resourceBody: `
            ${resource.graphql.resourceBody}
            icon
            active
            last_activity
            primary_process_id
            size_bytes_workspace
            organization_id_organization{
                usage_count_flow_dev
                usage_count_flow_productive
                usage_count_connection_dev
                usage_count_connection_productive
                usage_allowance_connection_productive
                usage_db_size_bytes
                usage_allowance_db_size_bytes
                allow_excess_usage
                usage_count_cde_minutes
            }
            branch_name
            git_url
            default_track_in_git
            force_track_in_git
            max_tracked_record_bytes
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            icon
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $icon: String,
            $branch_name: String
            $repository_name: String
            $default_track_in_git: Boolean
            $force_track_in_git: Boolean
            $max_tracked_record_bytes: BigInt
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            icon: $icon,
            branch_name: $branch_name
            repository_name: $repository_name
            default_track_in_git: $default_track_in_git
            force_track_in_git: $force_track_in_git
            max_tracked_record_bytes: $max_tracked_record_bytes
        `,
    },
    dashboardQueries: {
        executionStatusPerTime: executionStatusPerTimeDashboardQuery(),
        productiveExecutionStatusPerTime: executionStatusPerTimeDashboardQuery({
            is_productive: "true",
        }),
    },
    dashboardSubscriptions: {
        latestExecutions: () => ({
            subscription: executionsListSubscription,
            variables: {
                limit: 5,
                order: "-created_at",
            },
        }),
    },
    form: [],
    dashboard: [
        {
            uid: "workspace-dashboard-main",
            title: "Usage & activity",
            component: "CollapsibleSection",
            className: "w-full",
            defaultExpanded: true,
            items: [
                {
                    uid: "widget-1",
                    title: "Productive executions",
                    subtitle: "Last Week",
                    link: getWorkspaceExecutionFilterLink(productiveOnlyFilterField),
                    className: "w-1/3",
                    component: "CounterCard",
                    dataTestId: "productiveExecutions",
                    dto: ({ stats }: any) => {
                        const { execution_status_per_time: statData } =
                            stats?.productiveExecutionStatusPerTime || {};
                        return statData?.reduce((prev: any, cur: any) => prev + cur.count, 0);
                    },
                    omitWhen: checkSSPHidden() === false,
                },
                {
                    uid: "widget-cde-runtime",
                    title: "Total CDE runtime",
                    subtitle: "This month",
                    className: "w-1/3",
                    component: "CounterCard",
                    dto: ({ resourceData }: any) => {
                        const totalMinutes =
                            resourceData?.organization_id_organization?.usage_count_cde_minutes;
                        const totalHours = Math.floor(totalMinutes / 60);
                        const restMinutes = totalMinutes % 60;
                        return {
                            textValue:
                                totalMinutes === undefined
                                    ? "NA"
                                    : `${totalHours}h ${restMinutes}m`,
                        };
                    },
                    omitWhen: checkSSPHidden() === true,
                },
                {
                    uid: "widget-2",
                    title: "Connection usage",
                    subtitle: "This month",
                    className: "w-1/3",
                    component: "CounterCard",
                    dto: ({ resourceData }: any) => {
                        const org = resourceData?.organization_id_organization;
                        return {
                            textValue: `${
                                org?.usage_count_connection_productive >= 0
                                    ? org?.usage_count_connection_productive
                                    : "NA"
                            } / ${
                                org?.usage_allowance_connection_productive >= 0
                                    ? org?.usage_allowance_connection_productive
                                    : "NA"
                            }`,
                            valueRatio:
                                org?.usage_count_connection_productive /
                                org?.usage_allowance_connection_productive,
                        };
                    },
                },
                {
                    uid: "widget-3",
                    title: "Storage usage",
                    className: "w-1/3",
                    component: "CounterCard",
                    link: (
                        resourceID: string,
                        _record: any,
                        _userData: User,
                        userPermissions?: UserPermissionList[],
                    ) =>
                        hasOrganizationAdminPermission(userPermissions ?? [])
                            ? getWorkspaceLink(resourceID, "storage-analysis")
                            : null,
                    linkLabel: "Storage analysis",
                    dto: ({ resourceData }: any) => {
                        const org = resourceData?.organization_id_organization;
                        return {
                            textValue: `${
                                org?.usage_db_size_bytes >= 0
                                    ? prettyBytes(org?.usage_db_size_bytes, { binary: true })
                                    : "NA"
                            } / ${
                                org?.usage_allowance_db_size_bytes >= 0
                                    ? prettyBytes(org?.usage_allowance_db_size_bytes, {
                                          binary: true,
                                      })
                                    : "NA"
                            }`,
                            valueRatio:
                                org?.usage_db_size_bytes / org?.usage_allowance_db_size_bytes,
                        };
                    },
                },
                {
                    uid: "widget-4",
                    title: "Daily executions",
                    className: "w-2/3",
                    component: "OverTimeChart",
                    dataTestId: "dailyExecutions",
                    link: getWorkspaceExecutionFilterLink(),
                    dto: ({ stats }: any) =>
                        stats?.executionStatusPerTime
                            ? genericOverTimeChartDto(stats?.executionStatusPerTime)
                            : null,
                },
                {
                    uid: "widget-5",
                    title: "Execution ratio",
                    className: "w-1/3",
                    component: "PercentageChart",
                    dataTestId: "executionRatio",
                    link: getWorkspaceExecutionFilterLink(),
                    dto: ({ stats }: any) => {
                        const { execution_status_per_time: statData } =
                            stats?.executionStatusPerTime || {};

                        const totalExecutionsObj =
                            statData?.reduce(
                                (prev: any, cur: any) => ({
                                    ...prev,
                                    [cur.status]: cur.count + (prev[cur.status] || 0),
                                }),
                                {},
                            ) || {};

                        const totalSuccessExecutions =
                            totalExecutionsObj[ExecutionStatus.ENDED_SUCCESS] || 0;
                        const totalErrorExecutions =
                            totalExecutionsObj[ExecutionStatus.ENDED_ERROR] || 0;
                        const totalEndedExecutions = totalErrorExecutions + totalSuccessExecutions;

                        return totalEndedExecutions
                            ? Math.round((totalSuccessExecutions / totalEndedExecutions) * 100)
                            : null;
                    },
                },
                latestListContainerWidget(
                    "widget-6",
                    getWorkspaceExecutionFilterLink(),
                ),
                {
                    // Open comments assigned to the viewer, replacing the former
                    // "Successful executions" list (Margot, 2026-09-29;
                    // operator-task-fe-comment-assignment-make-useful).
                    uid: "widget-7",
                    title: "Assigned to me",
                    className: "w-1/3",
                    component: "AssignedComments",
                    dataTestId: "assignedToMe",
                    link: (_resourceID: string, _record: any, userData: User) =>
                        `${getWorkspaceLink("", advancedSearchPathFragment)}${buildAssignedCommentsSearchQuery(
                            userData["identity_id"],
                            getFilterString(
                                getFilterFieldsList(
                                    getSearchObj({
                                        value: advancedFilterResourceList,
                                        field: "resource_type",
                                        op: "in",
                                    })
                                )
                            )
                        )}`,
                    linkLabel: "Show all",
                },
                {
                    uid: "widget-8",
                    title: "Last edited by me",
                    className: "w-1/3",
                    component: "LatestEdits",
                    dataTestId: "latestEditedByMe",
                    link: (resourceID: string, record: any, userData: User) =>
                        buildLatestEditsQuery(userData["identity_id"]),
                },
            ],
        },
    ],
    hideCreateNew: true,
    detailsSidebar: [
        {
            label: "Database size",
            valueKey: "size_bytes_workspace",
            dto: (value: any) => <>{value ? prettyBytes(value, { binary: true }) : ""}</>,
        },
    ],
};
export default config;
